import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logService, Log } from '../../services/log.service';
import { format, isToday, isYesterday } from 'date-fns';
import { actions } from '../../store/actions';

export default function RecentActivityPanel({ onClose }: any) {
    const dispatch = useDispatch();
    const [logs, setLogs] = useState<Log[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchLogs();
        // Update last activity check timestamp when panel is opened
        dispatch(actions.setLastActivityCheck(new Date().toISOString()));
    }, [dispatch]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get logs from the last 24 hours
            const now = new Date();
            const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

            const response = await logService.getLogs({
                startDate: twentyFourHoursAgo.toISOString(),
                endDate: now.toISOString(),
                page: 1,
            });
            setLogs(response.logs);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch logs');
            console.error('Failed to fetch logs:', err);
        } finally {
            setLoading(false);
        }
    };

    function getIconAndColor(log: Log) {
        switch (log.module) {
            case 'auth':
                return { icon: 'mdi:account-key', color: '#3b82f6' };
            case 'inspection':
                return { icon: 'mdi:clipboard-check', color: '#10b981' };
            case 'inventory':
                return { icon: 'mdi:warehouse', color: '#f59e0b' };
            case 'ai':
                return { icon: 'mdi:robot', color: '#8b5cf6' };
            default:
                return { icon: 'mdi:information', color: '#6b7280' };
        }
    }

    const formatDateTime = (date: Date) => {
        const time = format(date, 'hh:mm a');
        if (isToday(date)) {
            return time;
        } else if (isYesterday(date)) {
            return `Yesterday, ${time}`;
        } else {
            return format(date, 'MMM d, yyyy, hh:mm a');
        }
    };

    const activities = logs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((log) => {
        const iconData = getIconAndColor(log);
        return {
            text: log.description,
            time: formatDateTime(new Date(log.createdAt)),
            icon: iconData.icon,
            color: iconData.color
        };
    });

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center w-full p-6" onClick={onClose} />
            <div className="fixed top-0 right-0 h-full w-[600px] xl:w-[636px] bg-white shadow-lg z-50 px-4 py-8">
                <div className="flex justify-between items-center px-4 py-4">
                    <div className='space-y-1'>
                        <h2 className="text-black-1 text-lg sm:text-xl md:text-[22px] xl:text-[24px] font-bold font-[Geist-Bold]">Recent Activity</h2>
                        <p className='text-black-1  text-sm xl:text-base font-[Acumin-Regular]'>You can see 24Hrs of activity only</p>
                    </div>

                    <button onClick={onClose} className="text-gray-500 hover:text-black text-xl">
                        <Icon
                            icon="gridicons:cross"
                            className="icon-color"
                            width={40}
                            height={20}
                        />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-2">Loading activities...</div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-500">
                            {error}
                            <button onClick={fetchLogs} className="ml-2 underline">
                                Retry
                            </button>
                        </div>
                    </div>
                ) : (
                    <ul>
                        {
                            activities.map((item, i) => {
                                return (
                                    <li key={i} className="flex items-center gap-4 px-4 py-3 text-sm">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: `${item.color}20`}}>
                                            <Icon icon={item.icon} className="text-xl" style={{color: item.color}} />
                                        </div>
                                        <div className="flex-1 justify-between flex items-center">
                                            <div className="text-black-1 text-sm xl:text-base font-[Acumin-Regular]">
                                                {item.text}
                                            </div>
                                            <div className="text-gray-2 text-sm xl:text-base font-[Acumin-Regular]">{item.time}</div>
                                        </div>
                                    </li>
                                )
                            })
                        }
                    </ul>
                )}
            </div>
        </>
    );
};